<script setup lang="ts">
  import { useActivityTracking, useAmplitude } from '@/shared/lib'
  import { PageDecorationImage } from '@/shared/ui/components'

  import { useProfile } from '@/entities/profile'

  import { FaqDescriptionCard, FaqList } from '@/views/faq'

  definePage({
    meta: { layout: 'baseNew' },
  })

  useHead({
    title: 'FAQ',
  })

  const router = useRouter()
  const { isAdmin, isContentManager } = storeToRefs(useProfile())
  const allowedToEdit = computed(() => isContentManager.value || isAdmin.value)

  const { track } = useAmplitude()
  const { updateActivity } = useActivityTracking()

  onMounted(() => {
    track('faq_section_engaged', {
      timestamp: Date.now(),
      user_role: isAdmin.value ? 'admin' : 'user',
      has_edit_permissions: allowedToEdit.value,
    })
    updateActivity('faq_section_engaged')
  })

  const trackCreateFaqClick = () => {
    track('faq_create_clicked', {
      timestamp: Date.now(),
      user_role: isAdmin.value ? 'admin' : 'user',
    })
    updateActivity('faq_create_clicked')
  }

  const trackFeedbackLinkClick = () => {
    track('faq_feedback_link_clicked', {
      timestamp: Date.now(),
      source: 'faq_page',
    })
    updateActivity('faq_feedback_link_clicked')
  }

  const handleAddClick = () => {
    trackCreateFaqClick()
    router.push('/faq/create-item')
  }
</script>

<template>
  <div
    class="wide-responsive relative w-full"
    data-testid="faq-page"
  >
    <PageDecorationImage />

    <div class="border-border bg-surface-bg flex items-center justify-between border-b px-6 pt-8 pb-6">
      <h1 class="heading-md text-heading!">FAQ</h1>

      <NButton
        v-if="allowedToEdit"
        secondary
        strong
        size="small"
        type="primary"
        class="shrink-0 [&_.n-button\_\_content]:gap-1.5"
        data-testid="faq-add-button"
        @click="handleAddClick"
      >
        <i
          class="fa-regular fa-plus text-xs"
          aria-hidden="true"
        />
        {{ $t('faq_add_item') }}
      </NButton>
    </div>

    <div class="mx-auto flex w-full max-w-225 flex-col gap-4 p-4">
      <FaqDescriptionCard
        v-if="!allowedToEdit"
        @feedback-click="trackFeedbackLinkClick"
      />
      <FaqList />
    </div>
  </div>
</template>
